import { Injectable, OnDestroy } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  ACTION_TARGET,
  Careplan,
  FHIRHelper,
  ICareplan,
  ICareplanKnowledgeInfos,
  IKnowledgesCriteria,
  ILink2Careplan,
  IObservationParam,
  IReference,
  ITranslation,
  KNOW_DOC_CATEGORY,
  Link2Careplan,
  MainCareplanReference,
  Period,
  Reference,
  VersioningStatus,
} from "@comunicare/comunicare-interfaces";
import { ICareplanEditorService } from "@comunicare/comunicare-interfaces-dashboard-services";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { BehaviorSubject, forkJoin, from, Observable, of, Subject } from "rxjs";
import { catchError, concatMap, finalize, first, map, takeUntil, tap } from "rxjs/operators";
import { CareplanHelper } from "src/app/helpers/careplan-helper";
import { LanguagesApiService } from "src/app/providers/api/languages-api.service";
import { CareplansService } from "src/app/providers/careplans.service";
import { KnowledgeCriteriaService } from "src/app/providers/knowledge-criteria-service";
import { LanguagesService } from "src/app/providers/languages.service";
import { UserService } from "src/app/providers/user.service";
import uuid from "uuid-random";
import { CareplanEditorApiService } from "../application/careplan-editor-api.service";
import { AVAILABLE_ACTIONS } from "../data/careplan-editor.enum";
import { CareplanTemplateFormHelper } from "./careplan-template-form-helper";
import { IlinkableObsItem, IlinkableQuestionnaireItem } from "./IlinkableItem.interface";

@Injectable({
  providedIn: "root",
})
export class CareplanEditorService implements OnDestroy, ICareplanEditorService {
  /** Holds the currently selected care plan template.*/
  public currentCareplanTemplate: ICareplan;
  /** Observable that signals when the careplan template is ready. */
  public careplanTemplateReady$ = new BehaviorSubject<boolean>(false);
  /** Observable that signals when the link to careplan is ready. */
  public link2CareplanReady$ = new Subject<boolean>();
  /** Form group for managing link2Careplan. */
  public link2CareplanForm: UntypedFormGroup;

  public careplanTemplateForm: UntypedFormGroup;
  public lastSaveDate: number;
  public availableLangs: ITranslation[];
  private onDestroy$ = new Subject<void>();
  public isJsonView = false;
  /** True while the careplan template is being saved (or published): the save buttons must be disabled */
  public isSaving$ = new BehaviorSubject<boolean>(false);
  /** Number of saves in progress (save and silentSave can overlap) */
  private nbSavesInProgress = 0;
  /** Prefix of the translation terms created by the careplan editor */
  public static readonly EDITOR_TERM_PREFIX = "careplanEditor/";
  /**
   * Suffix of the translation terms owned by a draft: the draft uses "careplanEditor/X/draft" while the published versions
   * (active and retired) share "careplanEditor/X". The server copies the shared terms when the draft is created and writes
   * the draft terms back to the shared terms when the draft is published.
   */
  public static readonly DRAFT_TERM_SUFFIX = "/draft";
  /** Translations loaded from the DB for the current draft (by term), used to detect if the user changed them */
  private loadedTranslations = new Map<string, ITranslation>();
  /** Term stored in the careplanTemplate for each translatable field of the form (set when the translations are initialized) */
  private translationTermByForm = new WeakMap<UntypedFormGroup, string>();

  constructor(
    private careplanEditorApi: CareplanEditorApiService,
    private userService: UserService,
    private fb: UntypedFormBuilder,
    private careplanTemplateFormHelper: CareplanTemplateFormHelper,
    private languageApiService: LanguagesApiService,
    private languagesService: LanguagesService,
    private router: Router,
    private careplanService: CareplansService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private knowledgeCriteriaService: KnowledgeCriteriaService
  ) {
    this.languagesService
      .list()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((languages) => {
        this.availableLangs = languages;
      });
    this.initLink2CareplanForm();
  }
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Retrieves a care plan template.
   * @param careplanRef - Reference to the care plan.
   * @param lang - Language code for localization. Defaults to "fr".
   * @returns Observable emitting the careplan template.
   */
  public getCareplanTemplate(
    careplanRef: string,
    withDraft: boolean,
    publicationDate?: string
  ): Observable<{
    published: ICareplan;
    draft: ICareplan;
  }> {
    const routeName = this.careplanEditorApi.readRoutes[0];

    return this.userService.isAuthorized(routeName, "GET").pipe(
      first(),
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("CareplanEditorService", "User does not have access to: GET " + routeName);
          return of() as Observable<{
            published: ICareplan;
            draft: ICareplan;
          }>;
        }
        return this.careplanEditorApi.getCareplanTemplate(careplanRef, withDraft, publicationDate) as Observable<{
          published: ICareplan;
          draft: ICareplan;
        }>;
      })
    );
  }

  /**
   * Signals if the careplan template is ready.
   * @param value - Value indicating if the care plan template is ready.
   */
  public setCareplanTemplateReady(value: boolean): void {
    this.careplanTemplateReady$.next(value);
  }

  /**
   * Signals if the link2Careplan form is ready.
   */
  public setLink2CareplanReady(value: boolean): void {
    this.link2CareplanReady$.next(value);
  }

  /**
   * Creates an empty careplan template object.
   * @param services - List of services associated with the care plan.
   * @returns Created care plan template object.
   */
  public createCareplanTemplateObj(services: Reference[]): Careplan {
    const careplanRef = uuid();
    return {
      identifier: [{ use: "usual", system: "http://comunicare.io", value: "" }],
      support: [new Reference({ display: MainCareplanReference, reference: careplanRef })],
      status: "active",
      period: new Period(),
      description: "no-name",
      resourceType: "Careplan",
      participant: [new Reference()],
      activity: [],
      category: [
        {
          coding: [{ system: FHIRHelper.SYSTEM_SNOMED, display: careplanRef, code: careplanRef }],
          text: careplanRef,
        },
      ],
      subject: new Reference(),
      author: services,
      versioningStatus: VersioningStatus.DRAFT,
      contributor: [],
      entityStatus: [],
      supervised: false,
    };
  }

  public initCareplanTemplateForm(careplanTemplate: ICareplan): void {
    this.careplanTemplateForm = this.careplanTemplateFormHelper.createCareplanTemplateFormGroup(careplanTemplate);
  }

  /**
   * Creates an empty link2Careplan associated with a careplan Template
   * @param careplanRef
   * @returns Created empty link2Careplan object.
   */
  public createLink2CareplanObj(careplanRef: string): ILink2Careplan {
    return new Link2Careplan({
      careplan: careplanRef,
      questionnaires: [],
      observations: [],
      knowledges: [],
      vitalSignsDefinitions: [],
      versioningStatus: VersioningStatus.DRAFT,
      defaultNotifyOnInactivity: false,
    });
  }

  /**
   * Creates a careplan template.
   * @param careplanTemplate - Care plan template to create.
   * @returns Observable emitting the created care plan template.
   */
  public createCareplanTemplate(careplanTemplate: ICareplan): Observable<ICareplan> {
    return this.careplanEditorApi.createCareplanTemplate(careplanTemplate) as Observable<ICareplan>;
  }

  /**
   * Creates a link2Careplan.
   * @param careplanId - (support.reference)
   * @returns Observable emitting the created link2Careplan.
   */
  public createLink2Careplan(link2Careplan: ILink2Careplan): Observable<ILink2Careplan> {
    return this.careplanEditorApi.createLink2Careplan(link2Careplan) as Observable<ILink2Careplan>;
  }

  /**
   * Retrieves a link to a care plan.
   * @param careplanId - ID of the care plan.
   * @returns Observable emitting the link to the care plan.
   */
  public getLink2Careplan(
    careplanId: string,
    withDraft: boolean
  ): Observable<{
    published: ILink2Careplan;
    draft: ILink2Careplan;
  }> {
    const routeName = this.careplanEditorApi.readRoutes[1];

    return this.userService.isAuthorized(routeName, "GET").pipe(
      first(),
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("CareplanEditorService", "User does not have access to: GET " + routeName);
          return of() as Observable<{
            published: ILink2Careplan;
            draft: ILink2Careplan;
          }>;
        }
        return this.careplanEditorApi.getlink2Careplan(careplanId, withDraft) as Observable<{
          published: ILink2Careplan;
          draft: ILink2Careplan;
        }>;
      })
    );
  }

  /**
   * Initializes the form for managing the link to care plan.
   */
  public initLink2CareplanForm(): void {
    this.link2CareplanForm = this.fb.group({
      careplan: ["", Validators.required],
      questionnaires: [[]],
      observations: [[]],
      knowledges: [[]],
      vitalSignsDefinitions: [[]],
      defaultNotifyOnInactivity: [false],
    });
  }

  /*
   * Retrieves all knowledges of a careplan (activity knowledges and knowledges linked to a careplan)
   */
  public getCareplanKnowledgeInfos(
    careplanId: string,
    draft: boolean,
    docCategories: KNOW_DOC_CATEGORY[],
    publicationDate?: string
  ): Observable<ICareplanKnowledgeInfos[]> {
    const routeName = this.careplanEditorApi.readRoutes[0];

    return this.userService.isAuthorized(routeName, "GET").pipe(
      first(),
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("CareplanEditorService", "User does not have access to: GET " + routeName);
          return of() as Observable<ICareplanKnowledgeInfos[]>;
        }
        return this.careplanEditorApi.careplanKnowledgeInfos(careplanId, draft, docCategories, publicationDate) as Observable<
          ICareplanKnowledgeInfos[]
        >;
      })
    );
  }

  /**
   * Initialize the translation for each careplan form fields that can be a translated field
   * case 1 : the value is not a translate key (did not start with $TR$) --> we put the value on the form (and set isTranslated to false)
   * case 2 : the value is a translate key and the translation is found in the database --> we put the translated value on the form for each languages
   * case 3 : the value is a translate key but the translation is not found in the database --> we put the value (with the $TR$) on the form (and set isTranslated to false)
   */
  public initTraductions(availableLangs: string[]): Observable<ITranslation[]> {
    this.loadedTranslations = new Map<string, ITranslation>();
    return forkJoin([this.initCareplanFieldsTraductions(availableLangs), this.initAdministratorTraductions(availableLangs)]).pipe(
      map(([careplanFieldsTranslations]) => careplanFieldsTranslations)
    );
  }

  /**
   * Initialize the translation for each "simple" careplan form field (name, activity name/description, types).
   * @see initTraductions for the documentation of the 3 cases handled.
   */
  private initCareplanFieldsTraductions(availableLangs: string[]): Observable<ITranslation[]> {
    // terms will contains all value that can be translated
    // form will contains all form field corresponding to the term (translationsForm will be the translations formArray)
    const termsForms = this.getTranslationTermsAndForms();
    const terms = termsForms.terms;
    const form = termsForms.formGroups;
    const translationsForm = termsForms.translationsForms;

    // The form has just been built from the currentCareplanTemplate: keep the stored term of each field, used when saving
    // (fields can then be added or removed in the form, so the index can no longer be used to find the term)
    form.forEach((f, i) => this.translationTermByForm.set(f, terms[i]));

    // We now need to filter terms to keep only those that start with $TR$, for the others we can already set the form fields corresponding
    // we also need to format the terms : in the request we need to send a string with all terms separate by "," (and not an array)
    let termsFormatedFiltered = "";
    const indexAlreadySet = [];
    terms.forEach((t, i) => {
      if (Tools.isTranslateKey(t)) {
        termsFormatedFiltered += "," + t.substring(4);
      } else {
        // not translated (or no value for this field in the careplanTemplate)
        form[i].get("isTranslated").setValue(false);

        (translationsForm[i] as UntypedFormArray)
          .at(0)
          .get("translateKey")
          .setValue(t && t !== "no-name" ? t : "");
        (translationsForm[i] as UntypedFormArray).at(0).get("lang").setValue("fr");

        indexAlreadySet.push(i);
      }
    });
    if (!termsFormatedFiltered) return of([]); // no translation needed

    // request to get the translations
    return this.languageApiService.getTranslations(termsFormatedFiltered.substring(1)).pipe(
      first(),
      tap((result) => {
        result?.forEach((r) => this.loadedTranslations.set(r.term, r));
        translationsForm.forEach((t, i) => {
          // indexAlreadySet is all terms that was not a translateKey (started not with $TR$)
          if (!indexAlreadySet.includes(i)) {
            t.clear();
            // we try to find the translation result for the term of the translationsForm
            const resultIndex = result?.findIndex((r) => r.term === terms[i].substring(4));
            if (resultIndex !== -1) {
              const trad = result[resultIndex];
              Object.keys(trad).forEach((key) => {
                if (availableLangs.includes(key)) {
                  t.push(
                    new UntypedFormGroup({
                      translateKey: new UntypedFormControl(trad[key], Validators.required),
                      lang: new UntypedFormControl(key),
                    })
                  );
                }
              });
            } else {
              // translation not found, we put the value without translation to the form field corresponding
              form[i].get("isTranslated").setValue(false);
              t.push(
                new UntypedFormGroup({
                  translateKey: new UntypedFormControl(terms[i], Validators.required),
                  lang: new UntypedFormControl("fr"),
                })
              );
            }
          }
        });
        return result;
      })
    );
  }
  /**
   * @returns all formgroups and translationsForms for each careplan fields that can be a translated field,
   * with the corresponding values of the currentCareplanTemplate (same index, undefined if no value).
   * Note: the terms are only aligned with the form right after the form creation (see translationTermByForm).
   */
  private getTranslationTermsAndForms(): { terms: string[]; formGroups: UntypedFormGroup[]; translationsForms: UntypedFormArray[] } {
    // setup terms, form and translationsForm for the careplan name (this.currentCareplanTemplate.description)
    const terms = [this.currentCareplanTemplate.description];
    const formGroups = [this.careplanTemplateForm.get("description") as UntypedFormGroup];
    const translationsForms = [this.careplanTemplateForm.get("description").get("translations") as UntypedFormArray];

    // setup terms, form and translationsForm for all activities of the careplan, we have two fields to translate : activity name and description
    const activities = this.careplanTemplateForm.get("activity") as UntypedFormArray;
    activities.controls.forEach((a, index) => {
      const activity = this.currentCareplanTemplate.activity?.[index];
      terms.push(activity?.reference?.display, activity?.detail?.description);
      formGroups.push(...[a.get("reference").get("display") as UntypedFormGroup, a.get("detail").get("description") as UntypedFormGroup]);
      translationsForms.push(
        ...[
          a.get("reference").get("display").get("translations") as UntypedFormArray,
          a.get("detail").get("description").get("translations") as UntypedFormArray,
        ]
      );
    });

    // setup terms, form and translationsForm for all addresses (=types) of the careplan, field to translate : display
    const addresses = this.careplanTemplateForm.get("addresses") as UntypedFormArray;
    addresses.controls.forEach((a, index) => {
      terms.push(this.currentCareplanTemplate.addresses?.[index]?.display);
      formGroups.push(...[a.get("display") as UntypedFormGroup]);
      translationsForms.push(...[a.get("display").get("translations") as UntypedFormArray]);
    });

    return {
      terms,
      formGroups,
      translationsForms,
    };
  }

  /**
   * For fields that can be a translated one: create, update, delete translations if necessary
   * @param availableLangs - all languages that the translations can be
   */
  public saveTraductions(availableLangs: string[]): Observable<unknown> {
    const termsForms = this.getTranslationTermsAndForms();
    const forms = termsForms.formGroups;
    // stored term of each field (undefined for the fields added since the form creation)
    const terms = forms.map((f) => this.translationTermByForm.get(f));
    const translationsForms = termsForms.translationsForms;

    const translationsToAdd: ITranslation[] = [];
    const translationsToEdit: ITranslation[] = [];
    const translationsToDelete: string[] = [];

    forms.forEach((form, i) => {
      if (form.get("isTranslated").value) {
        if (Tools.isTranslateKey(terms?.[i])) {
          // there was already a translation
          const translations = translationsForms[i].value;
          const defaultLang = form.get("defaultLang").value;
          const newTranslation = this.formatTranslationFormToItranslation(translations, defaultLang, availableLangs, terms[i].substring(4));
          if (newTranslation) {
            this.dispatchExistingTranslation(newTranslation, translations, availableLangs, translationsToAdd, translationsToEdit);

            // set the term to the form before saving the careplan
            translationsForms[i]
              .at(0)
              .get("translateKey")
              .setValue("$TR$" + newTranslation.term);
          }
        } else {
          // there was no translation yet
          // create the formated translation and it add to translationsToAdd
          const translations = translationsForms[i].value;
          const defaultLang = form.get("defaultLang").value;
          const newTranslation = this.formatTranslationFormToItranslation(translations, defaultLang, availableLangs);
          if (newTranslation) {
            translationsToAdd.push(newTranslation);

            // set the term to the form before saving the careplan
            translationsForms[i]
              .at(0)
              .get("translateKey")
              .setValue("$TR$" + newTranslation.term);
          }
        }
      } else {
        if (Tools.isTranslateKey(terms[i]) && CareplanEditorService.isTranslationOwnedByDraft(terms[i].substring(4))) {
          // delete translate
          translationsToDelete.push(terms[i].substring(4));
        }
      }
    });

    // A term can be used by several fields of the draft: only delete it if no other field uses it
    const draftTerms = CareplanEditorService.collectTranslationTerms(this.currentCareplanTemplate);
    const termsToDelete = translationsToDelete.filter((term) => draftTerms.filter((t) => t === term).length === 1);

    const obs$: Observable<unknown>[] = [];
    if (termsToDelete.length) {
      obs$.push(this.languageApiService.deleteTranslations(termsToDelete.join(",")));
    }
    if (translationsToAdd?.length) {
      obs$.push(this.languageApiService.createTranslations(translationsToAdd));
    }
    if (translationsToEdit?.length) {
      obs$.push(this.languageApiService.updateTranslations(translationsToEdit));
    }
    // Administrators are a dynamic nested list, handled independently (writes $TR$ into the form synchronously here).
    obs$.push(this.saveAdministratorTraductions(availableLangs));
    if (obs$.length) {
      return forkJoin(obs$);
    }
    return of([]);
  }

  /**
   * Collect, for every drug activity, the translatable display sub-form of each administrator.
   * @returns for each administrator: its display formGroup, its translations formArray and the raw stored term.
   */
  private collectAdministratorDisplayForms(): { displayForm: UntypedFormGroup; translations: UntypedFormArray; term: string }[] {
    const administratorForms: { displayForm: UntypedFormGroup; translations: UntypedFormArray; term: string }[] = [];
    const activities = this.careplanTemplateForm.get("activity") as UntypedFormArray;
    activities?.controls.forEach((activity) => {
      const administrators = activity.get("detail.administratorsList") as UntypedFormArray;
      administrators?.controls.forEach((administrator) => {
        const displayForm = administrator.get("display") as UntypedFormGroup;
        administratorForms.push({
          displayForm,
          translations: displayForm.get("translations") as UntypedFormArray,
          term: administrator.get("displayTerm")?.value,
        });
      });
    });
    return administratorForms;
  }

  /**
   * Initialize the translations of every administrator display name (same 3 cases as initTraductions).
   */
  private initAdministratorTraductions(availableLangs: string[]): Observable<unknown> {
    const administratorForms = this.collectAdministratorDisplayForms();
    if (!administratorForms.length) return of([]);

    let termsFormatedFiltered = "";
    const indexAlreadySet: number[] = [];
    administratorForms.forEach((administrator, i) => {
      if (Tools.isTranslateKey(administrator.term)) {
        termsFormatedFiltered += "," + administrator.term.substring(4);
      } else {
        administrator.displayForm.get("isTranslated").setValue(false);
        administrator.translations
          .at(0)
          .get("translateKey")
          .setValue(administrator.term ? administrator.term : "");
        administrator.translations.at(0).get("lang").setValue("fr");
        indexAlreadySet.push(i);
      }
    });
    if (!termsFormatedFiltered) return of([]);

    return this.languageApiService.getTranslations(termsFormatedFiltered.substring(1)).pipe(
      first(),
      tap((result: ITranslation[]) => {
        result?.forEach((r) => this.loadedTranslations.set(r.term, r));
        administratorForms.forEach((administrator, i) => {
          if (indexAlreadySet.includes(i)) return;
          administrator.translations.clear();
          const trad = result?.find((r) => r.term === administrator.term.substring(4));
          if (trad) {
            Object.keys(trad).forEach((key) => {
              if (availableLangs.includes(key)) {
                administrator.translations.push(
                  new UntypedFormGroup({
                    translateKey: new UntypedFormControl(trad[key], Validators.required),
                    lang: new UntypedFormControl(key),
                  })
                );
              }
            });
          } else {
            // translation not found, put the raw value without translation
            administrator.displayForm.get("isTranslated").setValue(false);
            administrator.translations.push(
              new UntypedFormGroup({
                translateKey: new UntypedFormControl(administrator.term, Validators.required),
                lang: new UntypedFormControl("fr"),
              })
            );
          }
        });
      })
    );
  }

  /**
   * Create/update the translations of every administrator display name and write the $TR$ key back into the form.
   */
  private saveAdministratorTraductions(availableLangs: string[]): Observable<unknown> {
    const administratorForms = this.collectAdministratorDisplayForms();
    const translationsToAdd: ITranslation[] = [];
    const translationsToEdit: ITranslation[] = [];

    administratorForms.forEach((administrator) => {
      if (!administrator.displayForm.get("isTranslated").value) return;
      const alreadyTranslated = Tools.isTranslateKey(administrator.term);
      const newTranslation = this.formatTranslationFormToItranslation(
        administrator.translations.value,
        administrator.displayForm.get("defaultLang").value,
        availableLangs,
        alreadyTranslated ? administrator.term.substring(4) : undefined
      );
      if (!newTranslation) return;
      if (alreadyTranslated) {
        this.dispatchExistingTranslation(
          newTranslation,
          administrator.translations.value,
          availableLangs,
          translationsToAdd,
          translationsToEdit
        );
      } else {
        translationsToAdd.push(newTranslation);
      }
      // set the $TR$ term to the form before saving the careplan
      administrator.translations
        .at(0)
        .get("translateKey")
        .setValue("$TR$" + newTranslation.term);
    });

    const obs$: Observable<unknown>[] = [];
    if (translationsToAdd.length) {
      obs$.push(this.languageApiService.createTranslations(translationsToAdd));
    }
    if (translationsToEdit.length) {
      obs$.push(this.languageApiService.updateTranslations(translationsToEdit));
    }
    return obs$.length ? forkJoin(obs$) : of([]);
  }

  /**
   * Set the displayTerm of every administrator to the value saved in the careplanTemplate ($TR$ term or plain text).
   * Needed before reloading the translations without rebuilding the form, otherwise they are initialized from the old stored value.
   */
  private syncAdministratorDisplayTerms(): void {
    this.collectAdministratorDisplayForms().forEach((administrator) => {
      administrator.displayForm.parent?.get("displayTerm")?.setValue(administrator.translations.at(0)?.get("translateKey").value);
    });
  }

  /**
   * @returns all the translation terms (without $TR$) found in the object (duplicates included)
   */
  public static collectTranslationTerms(obj: unknown, terms: string[] = []): string[] {
    if (typeof obj === "string") {
      if (Tools.isTranslateKey(obj)) terms.push(obj.substring(4));
    } else if (obj && typeof obj === "object") {
      Object.values(obj).forEach((value) => CareplanEditorService.collectTranslationTerms(value, terms));
    }
    return terms;
  }

  /**
   * A translation can be modified or deleted only if it is owned by the draft ("careplanEditor/X/draft"):
   * the other ones are shared with the published and retired versions (and the patients' careplans that use them),
   * or with other careplans (global terms like "treatment-initiation").
   * @param term - the term without $TR$
   */
  public static isTranslationOwnedByDraft(term: string): boolean {
    return term.startsWith(CareplanEditorService.EDITOR_TERM_PREFIX) && term.endsWith(CareplanEditorService.DRAFT_TERM_SUFFIX);
  }

  /**
   * @returns true if the careplan template still uses terms created by the editor that are shared with the published versions
   * (drafts created before the "/draft" terms): saving the draft makes the server copy them to terms owned by the draft
   */
  public static hasSharedEditorTerms(careplanTemplate: ICareplan): boolean {
    return CareplanEditorService.collectTranslationTerms(careplanTemplate).some(
      (term) => term.startsWith(CareplanEditorService.EDITOR_TERM_PREFIX) && !CareplanEditorService.isTranslationOwnedByDraft(term)
    );
  }

  /** @returns a new term owned by the draft */
  private static newDraftTerm(): string {
    return CareplanEditorService.EDITOR_TERM_PREFIX + uuid() + CareplanEditorService.DRAFT_TERM_SUFFIX;
  }

  /**
   * For a field that already had a translation, decide what to do with the translation from the form:
   *  - not changed by the user: nothing to save, the term is kept
   *  - owned by the draft: the translation is updated
   *  - shared (global term, or published version not yet copied for the draft): a new term is created for the draft
   *    (the newTranslation.term is changed)
   */
  private dispatchExistingTranslation(
    newTranslation: ITranslation,
    formTranslations: { translateKey: string; lang: string }[],
    availableLangs: string[],
    translationsToAdd: ITranslation[],
    translationsToEdit: ITranslation[]
  ): void {
    // compare the form with the loaded translation the same way the form was filled (see initTraductions)
    const loadedTranslation = this.loadedTranslations.get(newTranslation.term);
    const loadedLangs = loadedTranslation ? Object.keys(loadedTranslation).filter((key) => availableLangs.includes(key)) : [];
    const isUnchanged =
      loadedTranslation &&
      loadedLangs.length === formTranslations.length &&
      formTranslations.every((t) => loadedTranslation[t.lang] === t.translateKey);
    if (isUnchanged) return;

    if (CareplanEditorService.isTranslationOwnedByDraft(newTranslation.term)) {
      translationsToEdit.push(newTranslation);
    } else {
      newTranslation.term = CareplanEditorService.newDraftTerm();
      translationsToAdd.push(newTranslation);
    }
  }

  /**
   * @param translations - the array containing all translations
   * @param defaultLang - the lang for the languages not translated by the user in the form
   * @returns the ITranslation based on the form
   */
  public formatTranslationFormToItranslation(
    translations: [{ translateKey: string; lang: string }],
    defaultLang: string,
    availableLangs: string[],
    term?: string
  ): ITranslation {
    if (translations.findIndex((t) => Tools.isTranslateKey(t.translateKey)) !== -1) {
      // there is a $TR$ on the translation, don't save it
      return null;
    }
    const translationFormated: ITranslation = {
      term: term ? term : CareplanEditorService.newDraftTerm(),
      fr: "",
      it: "",
      de: "",
      nl: "",
      en: "",
      pt: "",
      tr: "",
    };
    availableLangs.forEach((lang) => {
      const translationFound = translations.find((t) => t.lang === lang);
      if (translationFound) {
        translationFormated[lang] = translationFound.translateKey;
      } else {
        // lang not set by user, we put the default lang
        translationFormated[lang] = translations.find((t) => t.lang === defaultLang).translateKey;
      }
    });
    return translationFormated;
  }

  /**
   * update a careplan template.
   * @param careplanTemplate - Care plan template to edit.
   * @returns Observable emitting the updated care plan template.
   */
  public updateCareplanTemplate(careplanTemplate: ICareplan): Observable<ICareplan> {
    return this.careplanEditorApi.updateCareplanTemplate(careplanTemplate) as Observable<ICareplan>;
  }

  /**
   * publish a careplan template.
   * @param careplanTemplateRef - Care plan template ref (support.reference) to publish.
   * @returns Observable emitting the published care plan template.
   */
  public publishCareplanTemplate(careplanTemplateRef: string): Observable<ICareplan> {
    return this.careplanEditorApi.publishCareplanTemplate(careplanTemplateRef) as Observable<ICareplan>;
  }

  /**
   * publish a link2Careplan.
   * @param careplanTemplateRef - Care plan template ref (support.reference).
   * @returns Observable emitting the published link2Careplan template.
   */
  public publishLink2CareplanTemplate(careplanTemplateRef: string): Observable<ILink2Careplan> {
    return this.careplanEditorApi.publishLink2CareplanTemplate(careplanTemplateRef) as Observable<ILink2Careplan>;
  }

  /**
   * publish the knowledgeCriteria.
   * @param careplanTemplateRef - Care plan template ref (support.reference).
   */
  public publishKnowledgeCriteria(careplanTemplateRef: string): Observable<unknown> {
    return this.careplanEditorApi.publishKnowledgeCriteria(careplanTemplateRef) as Observable<unknown>;
  }

  /**
   * Save the careplan template and all the translations, publish it if needed
   * @param availableLangs - all the langs availables, we need it for the translations
   * @param publish - indicate if we need to publish the careplan template or not
   * @param close - indicate if we close the editor after the saved (if publish is true, we close automatically)
   * @param isJson - indicate if we save via the json views, in this case there is no need to get value from the form, it will save the currentCareplanTemplate
   */
  public save(publish = false, close?: boolean, isJsonImport = false, updateCriteria = false): void {
    const form = this.careplanTemplateForm;
    if (form.valid && this.currentCareplanTemplate.versioningStatus === VersioningStatus.DRAFT) {
      this.setCareplanTemplateReady(false);
      this.startSaving();
      const availableLangs = this.availableLangs.map((l) => l.term);
      // State before the save, restored if the draft cannot be saved
      // (saveTraductions replaces the first translation of each field by its $TR$ term in the form)
      const formSnapshot = form.getRawValue();
      const careplanTemplateSnapshot: ICareplan = JSON.parse(JSON.stringify(this.currentCareplanTemplate));
      let isSaved = false;
      this.saveTraductions(availableLangs)
        .pipe(
          first(),
          concatMap(() => {
            if (!this.isJsonView && !isJsonImport) {
              // set the currentCareplanTemplate with form values
              this.careplanTemplateFormHelper.updateCareplanTemplateWithForm(form, this.currentCareplanTemplate);
            }
            return this.updateCareplanTemplate(this.currentCareplanTemplate).pipe(first());
          }),
          concatMap(() => {
            isSaved = true;
            if (publish) {
              return from(this.publish(updateCriteria));
            }
            if (close) {
              this.router.navigate(["/careplansList"]);
              return of(undefined);
            }
            // we stay in the editor so we need to reinitialize it with the currentCareplanTemplate saved
            return this.reloadEditor(availableLangs);
          }),
          catchError((err) => {
            FileLogger.error("CareplanEditorService", "save() failed", err);
            if (!isSaved) {
              // nothing is saved: back to the state before the save, the form keeps the changes of the user
              this.snackBar.open(this.translateService.instant("page.careplanEditor.error.saveFailed"), "ok", { duration: 10000 });
              this.restoreCurrentCareplanTemplate(careplanTemplateSnapshot);
              form.patchValue(formSnapshot);
              this.setCareplanTemplateReady(true);
              return of(undefined);
            }
            // the draft is saved, the publication or the reload of the editor failed: reload the editor with the draft saved
            const errorKey = publish ? "page.careplanEditor.error.publishFailed" : "api.errors.unknowerror";
            this.snackBar.open(this.translateService.instant(errorKey), "ok", { duration: 10000 });
            return this.reloadEditor(availableLangs).pipe(
              catchError((reloadErr) => {
                FileLogger.error("CareplanEditorService", "save() - failed to reload the editor", reloadErr);
                this.setCareplanTemplateReady(true);
                return of(undefined);
              })
            );
          }),
          finalize(() => this.endSaving())
        )
        .subscribe();
    } else {
      this.showInvalidFormError();
    }
  }

  /**
   * Display the errors of the careplan template form and warn the user that it cannot be saved
   * (with a specific message when no service is selected, as the services list can be folded or on another tab)
   */
  public showInvalidFormError(): void {
    this.careplanTemplateForm.markAllAsTouched();
    const errorKey = this.careplanTemplateForm.get("services")?.invalid
      ? "page.careplanEditor.error.noService"
      : "page.careplanEditor.error.invalidForm";
    this.snackBar.open(this.translateService.instant(errorKey), "ok", { duration: 5000 });
  }

  /**
   * Restore the currentCareplanTemplate in place (the tabs keep a reference to it)
   */
  private restoreCurrentCareplanTemplate(snapshot: ICareplan): void {
    const current = this.currentCareplanTemplate as unknown as Record<string, unknown>;
    Object.keys(current).forEach((key) => delete current[key]);
    Object.assign(current, snapshot);
  }

  /**
   * Reinitialize the form and its translations with the currentCareplanTemplate saved
   */
  private reloadEditor(availableLangs: string[]): Observable<unknown> {
    this.initCareplanTemplateForm(this.currentCareplanTemplate);
    return this.initTraductions(availableLangs).pipe(
      tap(() => {
        this.setCareplanTemplateReady(true);
        this.lastSaveDate = moment.now();
      })
    );
  }

  private startSaving(): void {
    this.isSaving$.next(++this.nbSavesInProgress > 0);
  }

  private endSaving(): void {
    this.nbSavesInProgress = Math.max(0, this.nbSavesInProgress - 1);
    this.isSaving$.next(this.nbSavesInProgress > 0);
  }

  private async publish(updateCriteria: boolean): Promise<void> {
    // Criterias of the published criteria before the current publish process.
    let previousCriteria;
    if (updateCriteria) {
      try {
        previousCriteria = (
          await this.knowledgeCriteriaService.list(null, null, this.currentCareplanTemplate.support[0].reference).toPromise()
        ).published;
      } catch (error) {
        FileLogger.error("publish()", "Can't get previous knowledge criteria");
      }
    }
    const ref = this.currentCareplanTemplate.support[0].reference;
    const publish$ = [this.publishCareplanTemplate(ref), this.publishLink2CareplanTemplate(ref), this.publishKnowledgeCriteria(ref)];
    await forkJoin(publish$).pipe(first()).toPromise();
    this.router.navigate(["/careplansList"]);
    if (updateCriteria) this.updateCareplanWithKnowledgeCriteria(previousCriteria);
  }

  /**
   * Save careplan template and translations without reloading the page (the form is kept, only its translations are reloaded).
   * Should be used to save the careplanTemplate from the form without jump screens.
   * Make sure to call setupQuestionnaires() or setupObservations() afterwards to have coherent data
   */
  silentSave(): Promise<ICareplan> {
    const form = this.careplanTemplateForm;
    if (form.valid && this.currentCareplanTemplate.versioningStatus === VersioningStatus.DRAFT) {
      const availableLangs = this.availableLangs.map((l) => l.term);
      this.startSaving();
      // State before the save, restored if the draft cannot be saved (see save())
      const formSnapshot = form.getRawValue();
      const careplanTemplateSnapshot: ICareplan = JSON.parse(JSON.stringify(this.currentCareplanTemplate));
      let isSaved = false;
      // The translations must be saved first: they set the $TR$ terms in the form, which are then copied in the careplanTemplate
      // (otherwise the careplanTemplate is saved with the text of the first language instead of the translation term)
      return this.saveTraductions(availableLangs)
        .pipe(
          first(),
          concatMap(() => {
            this.careplanTemplateFormHelper.updateCareplanTemplateWithForm(form, this.currentCareplanTemplate);
            return this.updateCareplanTemplate(this.currentCareplanTemplate).pipe(first());
          }),
          // saveTraductions replaced the first translation of each field by its $TR$ term: reload the translations in the form
          concatMap((careplanTemplate) => {
            isSaved = true;
            this.syncAdministratorDisplayTerms();
            return this.initTraductions(availableLangs).pipe(map(() => careplanTemplate));
          }),
          catchError((err) => {
            FileLogger.error("CareplanEditorService", "silentSave() failed", err);
            if (isSaved) {
              // only the reload of the translations failed
              this.snackBar.open(this.translateService.instant("api.errors.unknowerror"), "ok", { duration: 10000 });
              return of(this.currentCareplanTemplate);
            }
            // nothing is saved: back to the state before the save, the form keeps the changes of the user (saved with the next save)
            this.snackBar.open(this.translateService.instant("page.careplanEditor.error.saveFailed"), "ok", { duration: 10000 });
            this.restoreCurrentCareplanTemplate(careplanTemplateSnapshot);
            form.patchValue(formSnapshot);
            return of(undefined);
          }),
          finalize(() => this.endSaving())
        )
        .toPromise();
    } else {
      this.showInvalidFormError();
    }
  }

  public async linkOrUnlinkItem(
    $event: {
      item: IlinkableQuestionnaireItem | IlinkableObsItem;
      items: IlinkableQuestionnaireItem[] | IlinkableObsItem[];
      action: AVAILABLE_ACTIONS;
    },
    actionTarget: ACTION_TARGET
  ): Promise<void> {
    const { item, items, action } = $event;

    if (item.origin === "link2careplan") {
      if (action === AVAILABLE_ACTIONS.unlink) {
        await this.handleUnlinkFromCareplan(item, items, actionTarget);
      } else if (action === AVAILABLE_ACTIONS.link || action === AVAILABLE_ACTIONS.edit) {
        await this.checkAndUnlinkIfActionResulting(item, items, actionTarget);
      }
    } else if (item.origin === "activity") {
      const currentActivityFG = this.careplanTemplateFormHelper.getActivityFormGroupByReference(
        this.careplanTemplateForm,
        item.originReference.reference
      );

      const actionResulting: UntypedFormArray = currentActivityFG.get("actionResulting") as UntypedFormArray;
      if (action === AVAILABLE_ACTIONS.unlink) {
        await this.handleUnlinkFromActivity(actionResulting, item);
      } else if (action === AVAILABLE_ACTIONS.link) {
        await this.handleLinkToActivity(actionResulting, item, actionTarget);
      }
    }
  }

  /**
   * Handles the linking of an item to an activity.
   */
  private async handleLinkToActivity(
    actionResulting: UntypedFormArray,
    item: IlinkableQuestionnaireItem | IlinkableObsItem,
    actionTarget: ACTION_TARGET
  ) {
    actionResulting.push(
      this.fb.control(
        CareplanHelper.newActionResulting(
          this.currentCareplanTemplate.support[0].reference,
          actionTarget === ACTION_TARGET.QUESTIONNAIRE
            ? (item as IlinkableQuestionnaireItem).subjectType
            : (item as IlinkableObsItem).reference,
          actionTarget === ACTION_TARGET.QUESTIONNAIRE
            ? (item as IlinkableQuestionnaireItem).identifier
            : (item as IlinkableObsItem).reference,
          actionTarget
        )
      )
    );
  }

  /**
   * Handles the unlinking of an item from an activity.
   */
  private async handleUnlinkFromActivity(actionResultings: UntypedFormArray, item: IlinkableQuestionnaireItem | IlinkableObsItem) {
    const foundIndex = actionResultings.value.findIndex((ar) => ar.detail?.reference?.reference === item.uniqueId);

    if (foundIndex !== -1) {
      actionResultings.removeAt(foundIndex);
    }
  }

  /**
   * Handles the unlinking of an item from the careplan.
   */
  public async handleUnlinkFromCareplan(
    item: IlinkableQuestionnaireItem | IlinkableObsItem,
    items: IlinkableQuestionnaireItem[] | IlinkableObsItem[],
    actionTarget: ACTION_TARGET
  ): Promise<void> {
    const actionResultings: UntypedFormArray = this.careplanTemplateForm.get("actionResulting") as UntypedFormArray;
    const foundIndex = actionResultings.value.findIndex((ar) => ar.detail.reference.reference === item.uniqueId);
    // if there is an action Resulting for that item we delete it
    if (foundIndex !== -1) {
      actionResultings.removeAt(foundIndex);
    }
    // we update the link2careplan in any case
    await this.updateLink2Careplan(items, actionTarget);
  }

  /**
   * Checks whether an item exists in actionResultings.
   * If it does, removes it from items and updates link2careplan.
   */
  public async checkAndUnlinkIfActionResulting(
    item: IlinkableQuestionnaireItem | IlinkableObsItem,
    items: IlinkableQuestionnaireItem[] | IlinkableObsItem[],
    actionTarget: ACTION_TARGET
  ): Promise<void> {
    const actionResultings = this.careplanTemplateForm.get("actionResulting") as UntypedFormArray;
    const foundIndex = actionResultings.value.findIndex((ar) => ar?.detail?.reference?.reference === item.uniqueId);

    if (foundIndex !== -1) {
      const updatedItems = (items as IlinkableQuestionnaireItem[]).filter((i) => i.uniqueId !== item.uniqueId);
      await this.updateLink2Careplan(updatedItems, actionTarget);
    } else {
      await this.updateLink2Careplan(items, actionTarget);
    }
  }

  /**
   * Handles the linking AND the unlinking of an element to the careplan.
   * This add or remove the item to the link2careplan when an item is dropped.
   * The properties are saved separately in a second time.
   */
  private async updateLink2Careplan(items: IlinkableQuestionnaireItem[] | IlinkableObsItem[], actionTarget: ACTION_TARGET) {
    if (actionTarget === ACTION_TARGET.QUESTIONNAIRE) {
      this.link2CareplanForm.patchValue({
        questionnaires: (items as IlinkableQuestionnaireItem[]).map((q: IlinkableQuestionnaireItem) => q.identifier),
      });
    } else if (actionTarget === ACTION_TARGET.OBSERVATION) {
      this.link2CareplanForm.patchValue({ observations: (items as IlinkableObsItem[]).map((q: IlinkableObsItem) => q.reference) });
    }

    try {
      await this.careplanService.updateLink2Careplan(this.link2CareplanForm.getRawValue()).toPromise();
    } catch (error) {
      FileLogger.error("careplan-editor service", "Cannot update link2careplan");
    }
  }

  /**
   * Reset careplan template and link2Careplan
   */
  public reset(): void {
    this.currentCareplanTemplate = undefined;
    this.loadedTranslations = new Map<string, ITranslation>();
    this.careplanTemplateReady$.next(false);
    this.link2CareplanForm.reset();
  }

  public async updateCareplanWithKnowledgeCriteria(previousCriteria: IKnowledgesCriteria[]): Promise<void> {
    try {
      // Fetch the knowledge criteria for the careplan after current publication.
      const criterias = (
        await this.knowledgeCriteriaService.list(null, null, this.currentCareplanTemplate.support[0].reference).toPromise()
      ).published;

      // Get the list of criteria that have been removed since the last publication
      const deletedCriteria = previousCriteria.filter(
        (obj1) => !criterias.some((obj2) => obj1.knowledgeIdentifier.value === obj2.knowledgeIdentifier.value)
      );

      // variable to detect if one of the updates or more have failed
      let someUpdateFailed = false;
      for (const criteria of [...criterias, ...deletedCriteria]) {
        // Await each updateCareplansWithKnCriteria call before proceeding to the next one
        try {
          await this.knowledgeCriteriaService
            .updateCareplansWithKnCriteria(this.currentCareplanTemplate.support[0].reference, criteria.knowledgeIdentifier.value)
            .toPromise();
        } catch (error) {
          FileLogger.error("updateCareplanWithKnowledgeCriteria", "Cannot save criteria for knowledge " + criteria.knowledgeIdentifier);
          someUpdateFailed = true;
        }
      }
      if (someUpdateFailed) {
        // Failure
        this.snackBar.open(this.translateService.instant("api.errors.unknowerror"), undefined, {
          duration: 3000,
          horizontalPosition: "center",
          verticalPosition: "bottom",
        });
      } else {
        // Success
        this.snackBar.open(this.translateService.instant("common.saveSuccess"), undefined, {
          duration: 3000,
          horizontalPosition: "center",
          verticalPosition: "bottom",
        });
      }
    } catch (error) {
      // Handle any unexpected errors
      FileLogger.error("updateCareplanWithKnowledgeCriteria", "An unexpected error occurred: " + error.message);
      this.snackBar.open(this.translateService.instant("api.errors.unknowerror"), undefined, {
        duration: 3000,
        horizontalPosition: "center",
        verticalPosition: "bottom",
      });
    }
  }

  public duplicateCareplanTemplate(careplanRef: string, description: string, services: IReference[]): Observable<ICareplan> {
    return this.careplanEditorApi.duplicateCareplanTemplate(careplanRef, description, services);
  }

  public async getTemplateObsParam(careplanRef: string, obsRef: string): Promise<IObservationParam> {
    return this.careplanEditorApi.getTemplateObsParam(careplanRef, obsRef).toPromise();
  }
  public putTemplateObsParams(careplanRef: string, param: IObservationParam): Promise<IObservationParam> {
    return this.careplanEditorApi.putTemplateObsParams(careplanRef, param).toPromise();
  }
  public postTemplateObsParams(careplanRef: string, param: IObservationParam): Promise<IObservationParam> {
    return this.careplanEditorApi.postTemplateObsParams(careplanRef, param).toPromise();
  }
  public deleteTemplateObsParams(careplanRef: string, paramRef: string): Promise<null> {
    return this.careplanEditorApi.deleteTemplateObsParams(careplanRef, paramRef).toPromise();
  }
}
